import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { 
  XCircle, 
  ArrowLeft, 
  Crown, 
  Package, 
  Tag, 
  Gift,
  HelpCircle,
  ArrowRight,
  Clock
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { RUSHD_PERKS } from '../types/rushd';

const RushdCancelled = () => {
  return (
    <>
      <Helmet>
        <title>Checkout Cancelled | JamPackd Rush'd</title>
        <meta name="description" content="Your Rush'd membership checkout was cancelled. Learn more about our premium benefits." />
      </Helmet>
      
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-100">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          {/* Cancellation Header */}
          <div className="text-center mb-12">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-gray-100 rounded-full mb-6">
              <XCircle className="w-8 h-8 text-gray-500" />
            </div>
            
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Checkout Cancelled
            </h1>
            
            <p className="text-xl text-gray-600 max-w-2xl mx-auto mb-8">
              No worries! Your checkout was cancelled and no payment was processed. 
              You can try again anytime or learn more about Rush'd benefits.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button asChild className="bg-jamaica-green hover:bg-jamaica-green/90">
                <Link to="/rushd/pricing">
                  <Crown className="w-4 h-4 mr-2" />
                  Try Again
                </Link>
              </Button>
              
              <Button asChild variant="outline">
                <Link to="/products">
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Continue Shopping
                </Link>
              </Button>
            </div>
          </div>
          
          {/* Why Rush'd? */}
          <Card className="mb-8">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-center justify-center">
                <Crown className="w-6 h-6 text-jamaica-green" />
                Why Join Rush'd?
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-3 gap-6 mb-8">
                {RUSHD_PERKS.map((perk) => {
                  const IconComponent = {
                    Package,
                    Tag,
                    Gift,
                  }[perk.icon] || Package;
                  
                  return (
                    <div key={perk.id} className="text-center">
                      <div className="w-12 h-12 bg-jamaica-green/10 rounded-xl flex items-center justify-center mx-auto mb-3">
                        <IconComponent className="w-6 h-6 text-jamaica-green" />
                      </div>
                      <h3 className="font-medium text-gray-900 mb-2">{perk.name}</h3>
                      <p className="text-sm text-gray-600">{perk.description}</p>
                    </div>
                  );
                })}
              </div>
              
              <div className="text-center">
                <p className="text-gray-600 mb-4">
                  Join thousands of satisfied customers who save on Express shipping with Rush'd membership.
                </p>
                <Button asChild className="bg-jamaica-green hover:bg-jamaica-green/90">
                  <Link to="/rushd/pricing">
                    View Pricing
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Link>
                </Button>
              </div>
            </CardContent>
          </Card>
          
          {/* Common Questions */}
          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <HelpCircle className="w-5 h-5 text-jamaica-green" />
                  Common Questions
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <h4 className="font-medium text-gray-900 mb-1">Can I cancel anytime?</h4>
                  <p className="text-sm text-gray-600">Yes! Cancel anytime with no commitment. Your benefits remain active until the end of your billing period.</p>
                </div>
                
                <div>
                  <h4 className="font-medium text-gray-900 mb-1">How much can I save?</h4>
                  <p className="text-sm text-gray-600">Rush'd members typically save $9-12 per Express shipping order, plus enjoy priority processing.</p>
                </div>
                
                <div>
                  <h4 className="font-medium text-gray-900 mb-1">When do benefits start?</h4>
                  <p className="text-sm text-gray-600">Immediately! Your member pricing and benefits are active as soon as you subscribe.</p>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Clock className="w-5 h-5 text-jamaica-green" />
                  No Rush? No Problem
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 mb-4">
                  Take your time to decide! Rush'd membership will be here whenever you're ready. 
                  You can always join later and start saving immediately.
                </p>
                
                <div className="space-y-3">
                  <Button asChild variant="outline" className="w-full">
                    <Link to="/products">
                      <Package className="w-4 h-4 mr-2" />
                      Browse Products
                    </Link>
                  </Button>
                  
                  <Button asChild variant="outline" className="w-full">
                    <Link to="/faq">
                      <HelpCircle className="w-4 h-4 mr-2" />
                      Read FAQ
                    </Link>
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
          
          {/* Savings Calculator */}
          <Card className="bg-gradient-to-r from-green-50 to-emerald-50 border-green-200">
            <CardHeader>
              <CardTitle className="text-center text-green-800">See Your Potential Savings</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-center">
                <p className="text-gray-700 mb-6">
                  Express shipping typically costs $35-50 per order. Rush'd members save up to 25% on every shipment.
                </p>
                
                <div className="grid sm:grid-cols-3 gap-4 text-center mb-6">
                  <div className="p-4 bg-white rounded-lg border">
                    <div className="text-2xl font-bold text-gray-900">$35-50</div>
                    <div className="text-sm text-gray-600">Regular Express Cost</div>
                  </div>
                  <div className="p-4 bg-jamaica-green/10 rounded-lg border border-jamaica-green/20">
                    <div className="text-2xl font-bold text-jamaica-green">$26-38</div>
                    <div className="text-sm text-jamaica-green font-medium">Rush'd Member Cost</div>
                  </div>
                  <div className="p-4 bg-green-100 rounded-lg border border-green-200">
                    <div className="text-2xl font-bold text-green-600">$9-12</div>
                    <div className="text-sm text-green-600 font-medium">You Save</div>
                  </div>
                </div>
                
                <p className="text-sm text-gray-600 mb-4">
                  Just 2-3 Express orders per year covers your membership cost!
                </p>
                
                <Button asChild className="bg-jamaica-green hover:bg-jamaica-green/90">
                  <Link to="/rushd/pricing">
                    Join Rush'd Now
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Link>
                </Button>
              </div>
            </CardContent>
          </Card>
          
          {/* Footer */}
          <div className="text-center mt-12">
            <p className="text-gray-600 mb-4">
              Still have questions? Check out our 
              <Link to="/faq" className="text-jamaica-green hover:underline mx-1">FAQ</Link>
              or contact our support team.
            </p>
            <p className="text-sm text-gray-500">
              We're here to help you get the most out of your JamPack'd experience! 🇯🇲
            </p>
          </div>
        </div>
      </div>
    </>
  );
};

export default RushdCancelled;